import { FC } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { UpdateSelfParams } from 'src/services/napi/updateSelf/interfaces/updateSelf.interface'
import { updateSelf } from 'src/services/napi/updateSelf'
import en from 'src/constants/en'
import {
  HOME_ROUTE,
  SETTINGS_ACCOUNT_ROUTE,
  SETTINGS_BILLING_ROUTE,
  SETTINGS_COMPANY_PROFILE_ROUTE,
  SETTINGS_SUBSCRIPTION_ROUTE,
} from 'src/constants/routes'
import { UpdateSelfPasswordParams } from 'src/services/napi/updateSelfPassword/interfaces/updateSelfPassword.interface'
import { updateSelfPassword } from 'src/services/napi/updateSelfPassword'
import { useGetSelfQuery } from 'src/reactQuery/useGetSelfQuery'
import { BackNavigationButton } from 'src/components/BackNavigationButton'
import { useCustomLink } from 'src/hooks/useCustomLink'
import YourAccount from './YourAccount'
import Subscription from './Subscription'
import BillingHistory from './BillingHistory'
import CompanyProfile from './CompanyProfile'
import SettingsNav from './SettingsNav'
import styles from './settings.module.scss'

// const marginLeft = ['0%', '20%', '40%', '60%', '80%']
// Company Profile sits second, next to the account it describes, rather than
// after billing.
const filterItems = [
  { name: 'Your Account', path: SETTINGS_ACCOUNT_ROUTE },
  { name: 'Company Profile', path: SETTINGS_COMPANY_PROFILE_ROUTE },
  { name: 'Subscription', path: SETTINGS_SUBSCRIPTION_ROUTE },
  { name: 'Billing History', path: SETTINGS_BILLING_ROUTE },
]

const getActiveTabFromPath = (pathname: string): string => {
  if (pathname === SETTINGS_SUBSCRIPTION_ROUTE) return 'Subscription'
  if (pathname === SETTINGS_BILLING_ROUTE) return 'Billing History'
  if (pathname === SETTINGS_COMPANY_PROFILE_ROUTE) return 'Company Profile'
  return 'Your Account'
}

export const Settings: FC = () => {
  const { refetch: refetchUser, data: userData } = useGetSelfQuery()
  const location = useLocation()
  const navigate = useNavigate()
  const homeURL = useCustomLink(HOME_ROUTE)
  const accountURL = useCustomLink(SETTINGS_ACCOUNT_ROUTE)

  const filter = getActiveTabFromPath(location.pathname)

  // From Subscription / Billing History the back arrow returns to the Your
  // Account tab; from Your Account itself it goes to the home page.
  const backURL = filter === 'Your Account' ? homeURL : accountURL

  const handleTabChange = (tabName: string) => {
    const tab = filterItems.find((item) => item.name === tabName)
    if (tab) {
      navigate(tab.path)
    }
  }

  // const { data: facebookData, updateData: updateFacebookData } =
  //   useFacebookConnect()

  const handleUpdateUser = async (
    params: UpdateSelfParams,
    successMessage: string = en.USER_UPDATE_SUCCESS_MESSAGE
  ): Promise<boolean> => {
    try {
      await updateSelf(params)

      refetchUser()

      toast.success(successMessage)

      return true
    } catch (error) {
      toast.error(en.USER_UPDATE_ERROR_MESSAGE)

      return false
    }
  }

  const handleUpdatePassword = async (
    params: UpdateSelfPasswordParams
  ): Promise<boolean> => {
    try {
      await updateSelfPassword(params)

      toast.success(en.USER_PASSWORD_UPDATE_SUCCESS_MESSAGE)

      return true
    } catch (error) {
      toast.error(en.USER_PASSWORD_UPDATE_ERROR_MESSAGE)

      return false
    }
  }

  // const onFacebookConnect = () => {
  //   facebookLogin({
  //     onCompleted: (response) => {
  //       updateFacebookData(response)
  //     },
  //     onError: () => {
  //       toast.error(en.FACEBOOK_LOGIN_ERROR_MESSAGE)
  //     },
  //   })
  // }

  // const onFacebookDisconnect = () => {
  //   facebookLogOut({
  //     onCompleted: () => {
  //       updateFacebookData(null)
  //     },
  //     onError: () => {
  //       toast.error(en.FACEBOOK_LOGOUT_ERROR_MESSAGE)
  //     },
  //   })
  // }

  return (
    <div className={styles.settings}>
      <div className={styles.headerSection}>
        <BackNavigationButton title={en.settings} url={backURL} />
        <p className={styles.headerSubtitle}>
          Manage Your Account Settings And Preferences
        </p>
      </div>

      <SettingsNav
        activeTab={filter}
        onTabChange={handleTabChange}
        tabs={filterItems}
      />

      {/* Temporarily commented */}
      {/* <div
        className={styles.filtersWrapper}
      >
        <div className={styles.filetrs}>
          <ul
            className={styles.pills}
          >
            {filterItems.map((item, index) => (
              <li
                key={item.name}
                className={classNames(styles.pill, 
                  filter === item.name && styles.pillActive)
                onClick={() => setFilter(item.name)}
              >
                  {item.name}
              </li>
            ))}
          </ul>

          {!editMode && (
            <Button
              onClick={() => setEditMode(true)}
            >
              Edit
            </Button>
          ) }
        </div>
      </div> */}

      {filter === 'Your Account' && userData && (
        <YourAccount
          handleUpdatePassword={handleUpdatePassword}
          handleUpdateUser={handleUpdateUser}
          userData={userData.user}
        />
      )}

      {filter === 'Subscription' && <Subscription />}

      {filter === 'Billing History' && <BillingHistory />}

      {filter === 'Company Profile' && <CompanyProfile />}
    </div>
  )
}
